/**
 * @fileoverview Ultra-responsive, 60 FPS Canvas 2D mobile fan view for 'Rasca y gana' (Scratcher) (Tile ID 43) (/scratcher).
 * Features mandatory character selection with GoalBase, fixed 500x500 virtual buffer with responsive CSS aspect-ratio,
 * destination-out circular scratching, throttled 300ms alpha-channel sampling (step=16px), touch blockers with countdown,
 * session replacement handling (Taco pattern), game over modal, and Screen WakeLock management.
 * 
 * @module components/Dashboard/Sections/ScratcherFan
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoalBase } from '../../Admin/Commons/GoalBase';
import {
    ConnectWebSocketForScratcherFan,
    DisconnectWebSocketForScratcherFan,
    SendScratcherJoinSlot,
    SendScratcherProgress,
    RequestScratcherFanState
} from '../../../utilities/WS/ScratcherFanWS';
import { GetConfig, GetTheme } from '../../../utilities/BL/Scratcher';

/**
 * Storage key for persisting chosen player identity.
 * @constant {string}
 */
const SESSION_STORAGE_PLAYER_KEY = 'scratcher.selectedPlayer';

/**
 * Storage key for persisting player nickname.
 * @constant {string}
 */
const SESSION_STORAGE_NICKNAME_KEY = 'scratcher.nickname';

/**
 * Fixed internal virtual resolution for the 2D scratch canvas buffer.
 * @constant {number}
 */
const VIRTUAL_CANVAS_RESOLUTION = 500;

/**
 * Sampling step size in pixels for grid-based alpha channel evaluation.
 * @constant {number}
 */
const ALPHA_SAMPLING_GRID_STEP = 16;

/**
 * Throttle interval in milliseconds for scratch progress computation and dispatch.
 * @constant {number}
 */
const PROGRESS_THROTTLE_INTERVAL_MS = 300;

/**
 * Default fallback colors and assets for the Scratcher dynamic.
 * @constant {Object}
 */
const DEFAULT_THEME_FALLBACK = {
    name: 'Rasca y Gana Clásico',
    player1Name: 'Lucho',
    player1Avatar: '/img/games/taco/pinsa/eldo-local.png',
    player1Color: '#00ffcc',
    player2Name: 'Tunita',
    player2Avatar: '/img/games/taco/pinsa/eldo-visitante.png',
    player2Color: '#ff0055',
    coinSize: 25,
    targetPercentage: 100,
    maxTimeSeconds: 20,
    showScratchTool: true,
    fontFamily: 'Bangers',
    fanBackgroundColor: '#0a0a16',
    fanTextColor: '#ffffff',
    scratchCoverImageUrl: '',
    scratchHiddenImageUrl: '/img/games/briefcase/premio.png'
};

/**
 * Resolves media file paths into fully qualified URLs.
 * 
 * @function resolveMediaUrl
 * @param {string|null} mediaPath - Relative or absolute media URL.
 * @returns {string} Fully qualified media URL.
 */
const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:') || mediaPath.startsWith('data:')) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
    const cleanPath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
    return apiBaseUrl ? `${apiBaseUrl}${cleanPath}` : cleanPath;
};

/**
 * ScratcherFan Main Component
 * 
 * @function ScratcherFan
 * @returns {React.ReactElement} The rendered mobile fan interface.
 */
export const ScratcherFan = () => {
    // =========================================================================
    // 1. ESTADO DE SELECCIÓN DE PERSONAJE Y NICKNAME
    // =========================================================================
    const [selectedPlayerId, setSelectedPlayerId] = useState(() => {
        return sessionStorage.getItem(SESSION_STORAGE_PLAYER_KEY) || '';
    });

    const [fanNickname, setFanNickname] = useState(() => {
        return sessionStorage.getItem(SESSION_STORAGE_NICKNAME_KEY) || '';
    });

    // =========================================================================
    // 2. ESTADO DE CONECTIVIDAD Y TEMA
    // =========================================================================
    const [connectionStatus, setConnectionStatus] = useState('idle'); // idle, connecting, connected, reconnecting, error
    const [activeTheme, setActiveTheme] = useState(DEFAULT_THEME_FALLBACK);

    // =========================================================================
    // 3. ESTADO DEL MOTOR DE JUEGO
    // =========================================================================
    const [engineState, setEngineState] = useState('idle'); // idle, cover, game_ready, countdown, playing, paused, finished
    const [countdownValue, setCountdownValue] = useState(3);
    const [remainingSeconds, setRemainingSeconds] = useState(20);
    const [scratchedPercentage, setScratchedPercentage] = useState(0);
    const [remotePlayers, setRemotePlayers] = useState([]);
    const [targetPercentage, setTargetPercentage] = useState(100);
    const [coinSize, setCoinSize] = useState(25);
    const [showScratchTool, setShowScratchTool] = useState(false);

    // =========================================================================
    // 4. ESTADOS DE MODALES (SUSTITUCIÓN Y RESULTADOS)
    // =========================================================================
    const [isSessionReplaced, setIsSessionReplaced] = useState(false);
    const [sessionReplacedMessage, setSessionReplacedMessage] = useState('');
    const [isGameOverModalOpen, setIsGameOverModalOpen] = useState(false);
    const [gameOverPayload, setGameOverPayload] = useState(null);

    // =========================================================================
    // 5. REFERENCIAS MUTABLES PARA RENDIMIENTO CANVAS & WAKELOCK
    // =========================================================================
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const coverImageRef = useRef(null);
    const isScratchingRef = useRef(false);
    const lastPointRef = useRef(null);
    const strokeCountRef = useRef(0);
    const lastSampleTimestampRef = useRef(0);
    const scratchedPercentageRef = useRef(0);
    const screenWakeLockSentinelRef = useRef(null);
    const [toolPosition, setToolPosition] = useState({ x: 0, y: 0, isVisible: false });

    // =========================================================================
    // 6. GESTIÓN DE SCREEN WAKELOCK (MANTENER PANTALLA ACTIVA)
    // =========================================================================
    const requestScreenWakeLock = useCallback(async () => {
        try {
            if (typeof window !== 'undefined' && 'wakeLock' in navigator && !screenWakeLockSentinelRef.current) {
                const wakeLock = await navigator.wakeLock.request('screen');
                screenWakeLockSentinelRef.current = wakeLock;

                wakeLock.addEventListener('release', () => {
                    screenWakeLockSentinelRef.current = null;
                });
            }
        } catch (wakeLockError) {
            // Non-blocking fallback if device restricts wake lock
        }
    }, []);

    const releaseScreenWakeLock = useCallback(async () => {
        try {
            if (screenWakeLockSentinelRef.current) {
                await screenWakeLockSentinelRef.current.release();
                screenWakeLockSentinelRef.current = null;
            }
        } catch (releaseError) {
            screenWakeLockSentinelRef.current = null;
        }
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && selectedPlayerId && engineState !== 'finished') {
                requestScreenWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            releaseScreenWakeLock();
        };
    }, [selectedPlayerId, engineState, requestScreenWakeLock, releaseScreenWakeLock]);

    // Bloqueo de desplazamiento, pull-to-refresh y gestos del navegador al jugar
    useEffect(() => {
        const preventTouchScroll = (event) => {
            if (event.target.closest('.scratcher-fan__canvas') || event.target.closest('.scratcher-fan__arena') || event.target.closest('.scratcher-fan')) {
                if (event.cancelable) {
                    event.preventDefault();
                }
            }
        };

        const preventContextMenu = (event) => {
            if (event.target.closest('.scratcher-fan')) {
                event.preventDefault();
            }
        };

        window.addEventListener('touchmove', preventTouchScroll, { passive: false });
        window.addEventListener('contextmenu', preventContextMenu);

        return () => {
            window.removeEventListener('touchmove', preventTouchScroll);
            window.removeEventListener('contextmenu', preventContextMenu);
        };
    }, []);

    // =========================================================================
    // 7. CARGA DE CONFIGURACIÓN INICIAL DEL TEMA
    // =========================================================================
    useEffect(() => {
        const fetchInitialConfiguration = async () => {
            try {
                const configResponse = await GetConfig();
                if (configResponse && configResponse.success && configResponse.data) {
                    let themeData = configResponse.data.activeTheme;
                    if (!themeData && configResponse.data.activeThemeId) {
                        const themeResponse = await GetTheme(configResponse.data.activeThemeId);
                        if (themeResponse && themeResponse.success && themeResponse.data) {
                            themeData = themeResponse.data;
                        }
                    }
                    if (themeData) {
                        setActiveTheme((prevTheme) => ({
                            ...prevTheme,
                            ...themeData
                        }));
                        if (themeData.coinSize) setCoinSize(themeData.coinSize);
                        if (themeData.targetPercentage) setTargetPercentage(themeData.targetPercentage);
                        if (themeData.showScratchTool !== undefined) setShowScratchTool(themeData.showScratchTool);
                    }
                }
            } catch (fetchError) {
                // Non-blocking fallback to default configuration
            }
        };

        fetchInitialConfiguration();
    }, []);

    // =========================================================================
    // 8. RESOLUCIÓN DEL JUGADOR ACTIVO Y AVATAR
    // =========================================================================
    const activePlayerData = useMemo(() => {
        if (!selectedPlayerId) return null;
        const normalizedId = selectedPlayerId.toLowerCase();

        // Check if there is matching telemetry for this player
        const remotePlayer = remotePlayers?.find(
            (p) => (p.playerId && p.playerId.toLowerCase() === normalizedId) || (p.slot && p.slot.toLowerCase() === normalizedId)
        );

        if (normalizedId === 'player1' || normalizedId === 'j1') {
            const p1Name = remotePlayer?.name?.trim() || activeTheme?.player1Name?.trim() || 'Jugador 1';
            return {
                id: 'player1',
                slot: 'J1',
                name: p1Name,
                avatarUrl: activeTheme?.player1Avatar || DEFAULT_THEME_FALLBACK.player1Avatar,
                color: activeTheme?.player1Color || DEFAULT_THEME_FALLBACK.player1Color
            };
        }

        if (normalizedId === 'player2' || normalizedId === 'j2') {
            const p2Name = remotePlayer?.name?.trim() || activeTheme?.player2Name?.trim() || 'Jugador 2';
            return {
                id: 'player2',
                slot: 'J2',
                name: p2Name,
                avatarUrl: activeTheme?.player2Avatar || DEFAULT_THEME_FALLBACK.player2Avatar,
                color: activeTheme?.player2Color || DEFAULT_THEME_FALLBACK.player2Color
            };
        }

        // Check in extra players
        if (Array.isArray(activeTheme?.extraPlayers)) {
            const extra = activeTheme.extraPlayers.find(
                (p) => (p.id && p.id.toLowerCase() === normalizedId) || (p.slot && p.slot.toLowerCase() === normalizedId)
            );
            if (extra) {
                return {
                    id: extra.id || selectedPlayerId,
                    slot: extra.slot || 'JX',
                    name: remotePlayer?.name || extra.name || `Jugador ${selectedPlayerId}`,
                    avatarUrl: extra.avatarUrl || activeTheme?.player1Avatar,
                    color: extra.color || '#ffbb00'
                };
            }
        }

        return {
            id: selectedPlayerId,
            slot: 'J1',
            name: remotePlayer?.name || activeTheme?.player1Name || 'Aficionado',
            avatarUrl: activeTheme?.player1Avatar || DEFAULT_THEME_FALLBACK.player1Avatar,
            color: '#00ffcc'
        };
    }, [selectedPlayerId, activeTheme, remotePlayers]);

    // =========================================================================
    // 9. DIBUJO Y RENDERIZADO DEL BUFFER METÁLICO DEL CANVAS
    // =========================================================================

    /**
     * Fills the canvas with the top scratch cover (either custom image or metallic silver gradient).
     * 
     * @function renderCanvasCover
     */
    const renderCanvasCover = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctxRef.current = ctx;

        // Reset composite operation to default for drawing initial cover
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, VIRTUAL_CANVAS_RESOLUTION, VIRTUAL_CANVAS_RESOLUTION);

        const coverUrl = activeTheme?.scratchCoverImageUrl;

        if (coverUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                ctx.drawImage(img, 0, 0, VIRTUAL_CANVAS_RESOLUTION, VIRTUAL_CANVAS_RESOLUTION);
                coverImageRef.current = img;
            };
            img.onerror = () => {
                // Fallback to metallic sheen texture
                renderMetallicSilverTexture(ctx);
            };
            img.src = resolveMediaUrl(coverUrl);
        } else {
            renderMetallicSilverTexture(ctx);
        }

        scratchedPercentageRef.current = 0;
        setScratchedPercentage(0);
        strokeCountRef.current = 0;
    }, [activeTheme?.scratchCoverImageUrl]);

    /**
     * Renders a premium metallic silver gradient cover with shiny texture and guide text.
     * 
     * @function renderMetallicSilverTexture
     * @param {CanvasRenderingContext2D} ctx - 2D context.
     */
    const renderMetallicSilverTexture = (ctx) => {
        // Base metallic gradient
        const gradient = ctx.createLinearGradient(0, 0, VIRTUAL_CANVAS_RESOLUTION, VIRTUAL_CANVAS_RESOLUTION);
        gradient.addColorStop(0, '#d6d6d6');
        gradient.addColorStop(0.2, '#f5f5f5');
        gradient.addColorStop(0.4, '#a8a8a8');
        gradient.addColorStop(0.6, '#e0e0e0');
        gradient.addColorStop(0.8, '#8f8f8f');
        gradient.addColorStop(1, '#cccccc');

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, VIRTUAL_CANVAS_RESOLUTION, VIRTUAL_CANVAS_RESOLUTION);

        // Subtle glitter / shine lines
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 4;
        for (let diagonalOffset = -VIRTUAL_CANVAS_RESOLUTION; diagonalOffset < VIRTUAL_CANVAS_RESOLUTION * 2; diagonalOffset += 40) {
            ctx.beginPath();
            ctx.moveTo(diagonalOffset, 0);
            ctx.lineTo(diagonalOffset + VIRTUAL_CANVAS_RESOLUTION, VIRTUAL_CANVAS_RESOLUTION);
            ctx.stroke();
        }

        // Inner frame border
        ctx.strokeStyle = 'rgba(180, 180, 180, 0.7)';
        ctx.lineWidth = 12;
        ctx.strokeRect(10, 10, VIRTUAL_CANVAS_RESOLUTION - 20, VIRTUAL_CANVAS_RESOLUTION - 20);

        // Center scratch watermark
        ctx.save();
        ctx.fillStyle = '#666666';
        ctx.font = 'bold 38px Bangers, Oswald, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        ctx.shadowBlur = 2;
        ctx.fillText('¡RASCA AQUÍ!', VIRTUAL_CANVAS_RESOLUTION / 2, VIRTUAL_CANVAS_RESOLUTION / 2 - 20);

        ctx.font = 'bold 20px Oswald, sans-serif';
        ctx.fillStyle = '#888888';
        ctx.fillText('🪙 DESCUBRE TU PREMIO', VIRTUAL_CANVAS_RESOLUTION / 2, VIRTUAL_CANVAS_RESOLUTION / 2 + 25);
        ctx.restore();
    };

    // Re-initialize cover on mount or when returning to idle/game_ready
    useEffect(() => {
        if (selectedPlayerId) {
            renderCanvasCover();
        }
    }, [selectedPlayerId, renderCanvasCover]);

    // =========================================================================
    // 10. MUESTREO THROTTLED DEL ÁREA DESCUBIERTA (ALPHA SAMPLING STEP=16px)
    // =========================================================================

    /**
     * Evaluates the scratched ratio across a 16px grid on the 500x500 virtual buffer.
     * 
     * @function evaluateScratchedPercentage
     * @returns {number} Current scratched percentage (0 - 100).
     */
    const evaluateScratchedPercentage = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (!canvas || !ctx) return 0;

        try {
            const imageData = ctx.getImageData(0, 0, VIRTUAL_CANVAS_RESOLUTION, VIRTUAL_CANVAS_RESOLUTION);
            const data = imageData.data;
            let totalSampleCount = 0;
            let clearedSampleCount = 0;

            for (let y = 0; y < VIRTUAL_CANVAS_RESOLUTION; y += ALPHA_SAMPLING_GRID_STEP) {
                for (let x = 0; x < VIRTUAL_CANVAS_RESOLUTION; x += ALPHA_SAMPLING_GRID_STEP) {
                    const alphaPixelIndex = (y * VIRTUAL_CANVAS_RESOLUTION + x) * 4 + 3;
                    totalSampleCount += 1;
                    // If alpha is less than 128, pixel is considered cleared/scratched
                    if (data[alphaPixelIndex] < 128) {
                        clearedSampleCount += 1;
                    }
                }
            }

            const rawCalculatedPercentage = totalSampleCount > 0 ? (clearedSampleCount / totalSampleCount) * 100 : 0;
            const smoothedPercentage = Math.round(rawCalculatedPercentage * 10) / 10;

            // Enforce monotonic progression (never decrease)
            const finalProgressPercentage = Math.min(100, Math.max(scratchedPercentageRef.current, smoothedPercentage));
            scratchedPercentageRef.current = finalProgressPercentage;
            setScratchedPercentage(finalProgressPercentage);

            // Transmit progress to server via WebSocket
            if (selectedPlayerId) {
                SendScratcherProgress(selectedPlayerId, finalProgressPercentage, strokeCountRef.current);
            }

            // If player completed the target percentage, automatically clear canvas to reveal prize
            if (finalProgressPercentage >= targetPercentage) {
                ctx.save();
                ctx.globalCompositeOperation = 'destination-out';
                ctx.fillRect(0, 0, VIRTUAL_CANVAS_RESOLUTION, VIRTUAL_CANVAS_RESOLUTION);
                ctx.restore();
            }

            return finalProgressPercentage;
        } catch (samplingError) {
            return scratchedPercentageRef.current;
        }
    }, [selectedPlayerId, targetPercentage]);

    // =========================================================================
    // 11. MANEJO DE EVENTOS TÁCTILES Y DIBUJO DESTINATION-OUT
    // =========================================================================

    /**
     * Translates client pointer/touch coordinates to 500x500 virtual buffer coordinates.
     * 
     * @function getVirtualCoordinates
     * @param {TouchEvent|MouseEvent} event - Native DOM or React synthetic event.
     * @returns {{x: number, y: number, clientX: number, clientY: number}|null} Scaled coordinates.
     */
    const getVirtualCoordinates = useCallback((event) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;

        const boundingRectangle = canvas.getBoundingClientRect();
        let clientX = 0;
        let clientY = 0;

        if (event.touches && event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else if (event.changedTouches && event.changedTouches.length > 0) {
            clientX = event.changedTouches[0].clientX;
            clientY = event.changedTouches[0].clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }

        const scaleRatioX = VIRTUAL_CANVAS_RESOLUTION / boundingRectangle.width;
        const scaleRatioY = VIRTUAL_CANVAS_RESOLUTION / boundingRectangle.height;

        const virtualX = (clientX - boundingRectangle.left) * scaleRatioX;
        const virtualY = (clientY - boundingRectangle.top) * scaleRatioY;

        return {
            x: Math.min(VIRTUAL_CANVAS_RESOLUTION, Math.max(0, virtualX)),
            y: Math.min(VIRTUAL_CANVAS_RESOLUTION, Math.max(0, virtualY)),
            clientX,
            clientY
        };
    }, []);

    /**
     * Performs a single destination-out scratch stroke on the virtual canvas.
     * 
     * @function performScratchStroke
     * @param {{x: number, y: number}} currentCoord - Current virtual point.
     * @param {{x: number, y: number}|null} previousCoord - Previous virtual point.
     */
    const performScratchStroke = useCallback((currentCoord, previousCoord) => {
        const ctx = ctxRef.current;
        if (!ctx) return;

        const strokeRadius = coinSize || 25;

        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = strokeRadius * 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        ctx.beginPath();
        if (previousCoord) {
            ctx.moveTo(previousCoord.x, previousCoord.y);
            ctx.lineTo(currentCoord.x, currentCoord.y);
            ctx.stroke();
        } else {
            ctx.arc(currentCoord.x, currentCoord.y, strokeRadius, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();

        strokeCountRef.current += 1;

        // Tactical haptic vibration on mobile devices
        if (typeof window !== 'undefined' && 'navigator' in window && 'vibrate' in navigator) {
            try {
                if (strokeCountRef.current % 5 === 0) {
                    navigator.vibrate(8);
                }
            } catch (hapticError) {
                // Ignore
            }
        }
    }, [coinSize]);

    // Touch & Mouse Event Handlers
    const handleScratchStart = useCallback((event) => {
        if (engineState !== 'playing') return;
        event.preventDefault();

        const coords = getVirtualCoordinates(event);
        if (!coords) return;

        isScratchingRef.current = true;
        lastPointRef.current = { x: coords.x, y: coords.y };
        setToolPosition({ x: coords.clientX, y: coords.clientY, isVisible: true });

        performScratchStroke(coords, null);
    }, [engineState, getVirtualCoordinates, performScratchStroke]);

    const handleScratchMove = useCallback((event) => {
        if (!isScratchingRef.current || engineState !== 'playing') return;
        event.preventDefault();

        const coords = getVirtualCoordinates(event);
        if (!coords) return;

        performScratchStroke(coords, lastPointRef.current);
        lastPointRef.current = { x: coords.x, y: coords.y };
        setToolPosition({ x: coords.clientX, y: coords.clientY, isVisible: true });

        // Throttled grid sampling every 300ms
        const now = Date.now();
        if (now - lastSampleTimestampRef.current >= PROGRESS_THROTTLE_INTERVAL_MS) {
            lastSampleTimestampRef.current = now;
            evaluateScratchedPercentage();
        }
    }, [engineState, getVirtualCoordinates, performScratchStroke, evaluateScratchedPercentage]);

    const handleScratchEnd = useCallback((event) => {
        if (!isScratchingRef.current) return;
        isScratchingRef.current = false;
        lastPointRef.current = null;
        setToolPosition((prev) => ({ ...prev, isVisible: false }));

        // Final sampling when finger lifts
        evaluateScratchedPercentage();
    }, [evaluateScratchedPercentage]);

    // =========================================================================
    // 12. COMUNICACIÓN WEBSOCKET Y PROCESAMIENTO DE MENSAJES
    // =========================================================================

    const handleWebSocketMessage = useCallback((payload) => {
        if (!payload || !payload.type) return;

        switch (payload.type) {
            case 'scratcher_snapshot':
                if (payload.state) {
                    setEngineState(payload.state.state || 'idle');
                    if (payload.state.activeTheme) {
                        setActiveTheme((prev) => ({ ...prev, ...payload.state.activeTheme }));
                    }
                    if (payload.state.players) {
                        setRemotePlayers(payload.state.players);
                    }
                    if (payload.state.targetPercentage) setTargetPercentage(payload.state.targetPercentage);
                    if (payload.state.coinSize) setCoinSize(payload.state.coinSize);
                    if (payload.state.showScratchTool !== undefined) setShowScratchTool(payload.state.showScratchTool);
                    if (payload.state.remainingTimeSeconds !== undefined) setRemainingSeconds(payload.state.remainingTimeSeconds);
                }
                break;

            case 'scratcher_countdown':
                setEngineState('countdown');
                setCountdownValue(payload.count !== undefined ? payload.count : 3);
                break;

            case 'scratcher_game_started':
                setEngineState('playing');
                setCountdownValue(0);
                if (payload.maxTimeSeconds) setRemainingSeconds(payload.maxTimeSeconds);
                if (payload.targetPercentage) setTargetPercentage(payload.targetPercentage);
                renderCanvasCover();
                break;

            case 'scratcher_paused':
                setEngineState('paused');
                if (payload.remainingTimeSeconds !== undefined) {
                    setRemainingSeconds(payload.remainingTimeSeconds);
                }
                break;

            case 'scratcher_resumed':
                setEngineState('playing');
                if (payload.remainingTimeSeconds !== undefined) {
                    setRemainingSeconds(payload.remainingTimeSeconds);
                }
                break;

            case 'scratcher_progress_sync':
                if (payload.data && Array.isArray(payload.data.players)) {
                    setRemotePlayers(payload.data.players);
                } else if (payload.state && Array.isArray(payload.state.players)) {
                    setRemotePlayers(payload.state.players);
                } else if (Array.isArray(payload.players)) {
                    setRemotePlayers(payload.players);
                }
                break;

            case 'scratcher_update_names':
            case 'scratcher_set_player_names':
                if (payload.player1Name || payload.player2Name) {
                    setActiveTheme((prev) => ({
                        ...prev,
                        player1Name: payload.player1Name || prev.player1Name,
                        player2Name: payload.player2Name || prev.player2Name
                    }));
                }
                if (payload.players && Array.isArray(payload.players)) {
                    setRemotePlayers(payload.players);
                } else if (payload.state && Array.isArray(payload.state.players)) {
                    setRemotePlayers(payload.state.players);
                }
                break;

            case 'scratcher_players_updated':
            case 'scratcher_telemetry':
                if (payload.state && Array.isArray(payload.state.players)) {
                    setRemotePlayers(payload.state.players);
                } else if (payload.players && Array.isArray(payload.players)) {
                    setRemotePlayers(payload.players);
                }
                if (payload.state && payload.state.activeTheme) {
                    setActiveTheme((prev) => ({ ...prev, ...payload.state.activeTheme }));
                }
                break;

            case 'scratcher_session_replaced':
                setIsSessionReplaced(true);
                setSessionReplacedMessage(payload.message || 'Tu sesión fue transferida a otro dispositivo. ¡Gracias por participar!');
                DisconnectWebSocketForScratcherFan();
                break;

            case 'scratcher_game_over':
                setEngineState('finished');
                setIsGameOverModalOpen(true);
                setGameOverPayload(payload);
                break;

            case 'scratcher_show_cover':
                setEngineState('cover');
                renderCanvasCover();
                break;

            case 'scratcher_show_game':
                setEngineState('game_ready');
                renderCanvasCover();
                break;

            case 'scratcher_reset':
                setEngineState('idle');
                setIsGameOverModalOpen(false);
                setGameOverPayload(null);
                if (payload.state && Array.isArray(payload.state.players)) {
                    setRemotePlayers(payload.state.players);
                }
                renderCanvasCover();
                break;

            default:
                break;
        }
    }, [renderCanvasCover]);

    // WebSocket connection lifecycle
    useEffect(() => {
        ConnectWebSocketForScratcherFan(
            handleWebSocketMessage,
            (newStatus) => setConnectionStatus(newStatus),
            selectedPlayerId || null,
            fanNickname || null
        );

        if (selectedPlayerId) {
            requestScreenWakeLock();
        }

        return () => {
            DisconnectWebSocketForScratcherFan();
            releaseScreenWakeLock();
        };
    }, [selectedPlayerId, fanNickname, handleWebSocketMessage, requestScreenWakeLock, releaseScreenWakeLock]);

    // =========================================================================
    // 13. SELECCIÓN DE PERSONAJE (ACCIÓN DE USUARIO)
    // =========================================================================

    const handleSelectPlayer = (playerId) => {
        setSelectedPlayerId(playerId);
        sessionStorage.setItem(SESSION_STORAGE_PLAYER_KEY, playerId);
        if (fanNickname) {
            sessionStorage.setItem(SESSION_STORAGE_NICKNAME_KEY, fanNickname);
        }
        SendScratcherJoinSlot(playerId, fanNickname);
        renderCanvasCover();
        requestScreenWakeLock();
    };

    const handleSwitchCharacter = () => {
        sessionStorage.removeItem(SESSION_STORAGE_PLAYER_KEY);
        setSelectedPlayerId('');
        releaseScreenWakeLock();
        DisconnectWebSocketForScratcherFan();
        setConnectionStatus('idle');
        setEngineState('idle');
        setScratchedPercentage(0);
        scratchedPercentageRef.current = 0;
        strokeCountRef.current = 0;
        setIsGameOverModalOpen(false);
        setIsSessionReplaced(false);
    };

    // =========================================================================
    // 14. PANTALLA DE SELECCIÓN DE PERSONAJE (GOALBASE)
    // =========================================================================
    if (!selectedPlayerId) {
        const j1DisplayName = remotePlayers?.find((p) => p.playerId === 'player1' || p.slot === 'J1')?.name || activeTheme?.player1Name || 'Lucho';
        const j2DisplayName = remotePlayers?.find((p) => p.playerId === 'player2' || p.slot === 'J2')?.name || activeTheme?.player2Name || 'Tunita';

        return (
            <GoalBase classNameApplied={'Fan__Goal__Header'}>
                <div className="Fan__Goal">
                    <p className="Fan__Goal__Title">Selecciona tu personaje</p>

                    <div className="Fan__Goal__Players">
                        {/* JUGADOR 1 */}
                        <button
                            type="button"
                            className="Fan__Goal__Players__Buttons"
                            onClick={() => handleSelectPlayer('player1')}
                        >
                            <img
                                src={resolveMediaUrl(activeTheme?.player1Avatar || DEFAULT_THEME_FALLBACK.player1Avatar)}
                                className="Fan__Goal__Players__Buttons__Image"
                                alt={j1DisplayName}
                            />
                            <span className="Fan__Goal__Players__Buttons__Text">
                                {j1DisplayName}
                            </span>
                        </button>

                        {/* JUGADOR 2 */}
                        <button
                            type="button"
                            className="Fan__Goal__Players__Buttons"
                            onClick={() => handleSelectPlayer('player2')}
                        >
                            <img
                                src={resolveMediaUrl(activeTheme?.player2Avatar || DEFAULT_THEME_FALLBACK.player2Avatar)}
                                className="Fan__Goal__Players__Buttons__Image"
                                alt={j2DisplayName}
                            />
                            <span className="Fan__Goal__Players__Buttons__Text">
                                {j2DisplayName}
                            </span>
                        </button>

                        {/* EXTRA PLAYERS (si existen) */}
                        {Array.isArray(activeTheme?.extraPlayers) &&
                            activeTheme.extraPlayers.map((extraPlayer, index) => {
                                const extraDisplayName = remotePlayers?.find((p) => p.playerId === extraPlayer.id || p.slot === extraPlayer.slot)?.name || extraPlayer.name || `Jugador ${index + 3}`;
                                return (
                                    <button
                                        key={extraPlayer.id || `extra-${index}`}
                                        type="button"
                                        className="Fan__Goal__Players__Buttons"
                                        onClick={() => handleSelectPlayer(extraPlayer.id || `player${index + 3}`)}
                                    >
                                        <img
                                            src={resolveMediaUrl(extraPlayer.avatarUrl || activeTheme?.player1Avatar)}
                                            className="Fan__Goal__Players__Buttons__Image"
                                            alt={extraDisplayName}
                                        />
                                        <span className="Fan__Goal__Players__Buttons__Text">
                                            {extraDisplayName}
                                        </span>
                                    </button>
                                );
                            })}
                    </div>
                </div>
            </GoalBase>
        );
    }

    // =========================================================================
    // 15. INTERFAZ PRINCIPAL DE RASPADO (ARENA MÓVIL)
    // =========================================================================
    return (
        <div
            className="scratcher-fan"
            style={{
                backgroundColor: activeTheme?.fanBackgroundColor || '#0a0a16',
                fontFamily: activeTheme?.fontFamily || 'Bangers, Oswald, sans-serif'
            }}
        >
            {/* CABECERA SUPERIOR */}
            <header className="scratcher-fan__header">
                <div className="scratcher-fan__player-pill" style={{ borderColor: activePlayerData?.color }}>
                    <img
                        src={resolveMediaUrl(activePlayerData?.avatarUrl)}
                        alt={activePlayerData?.name}
                        className="scratcher-fan__player-thumb"
                    />
                    <div className="scratcher-fan__player-meta">
                        <span className="scratcher-fan__player-name" style={{ color: activePlayerData?.color }}>
                            {activePlayerData?.name}
                        </span>
                        <span className="scratcher-fan__player-slot">{activePlayerData?.slot}</span>
                    </div>
                </div>

                <div className="scratcher-fan__status-badge">
                    <span className={`scratcher-fan__dot scratcher-fan__dot--${connectionStatus}`} />
                    <span className="scratcher-fan__status-text">
                        {connectionStatus === 'connected' ? 'En Vivo' : connectionStatus === 'reconnecting' ? 'Reconectando' : 'Conectando'}
                    </span>
                </div>

                <button
                    type="button"
                    className="scratcher-fan__exit-btn"
                    onClick={handleSwitchCharacter}
                    title="Salir"
                >
                    <i className="bi bi-box-arrow-right" />
                </button>
            </header>

            {/* BARRA DE PROGRESO DEL JUGADOR */}
            <div className="scratcher-fan__progress-container">
                <div className="scratcher-fan__progress-meta">
                    <span className="scratcher-fan__progress-label">Área descubierta:</span>
                    <span className="scratcher-fan__progress-value" style={{ color: activePlayerData?.color }}>
                        {scratchedPercentage}% / {targetPercentage}%
                    </span>
                </div>
                <div className="scratcher-fan__progress-track">
                    <div
                        className="scratcher-fan__progress-bar"
                        style={{
                            width: `${Math.min(100, (scratchedPercentage / targetPercentage) * 100)}%`,
                            backgroundColor: activePlayerData?.color || '#00ffcc'
                        }}
                    />
                </div>
            </div>

            {/* CONTENEDOR DEL CANVAS DE RASPADO ULTRA-OPTIMIZADO */}
            <main className="scratcher-fan__arena">
                <div className="scratcher-fan__canvas-wrapper">
                    {/* CAPA DE PREMIO OCULTO (FONDO) */}
                    <div className="scratcher-fan__prize-layer">
                        <img
                            src={resolveMediaUrl(activeTheme?.scratchHiddenImageUrl || DEFAULT_THEME_FALLBACK.scratchHiddenImageUrl)}
                            alt="Premio Oculto"
                            className="scratcher-fan__prize-image"
                        />
                    </div>

                    {/* BUFFER VIRTUAL 2D 500x500 */}
                    <canvas
                        ref={canvasRef}
                        width={VIRTUAL_CANVAS_RESOLUTION}
                        height={VIRTUAL_CANVAS_RESOLUTION}
                        className="scratcher-fan__canvas"
                        onTouchStart={handleScratchStart}
                        onTouchMove={handleScratchMove}
                        onTouchEnd={handleScratchEnd}
                        onTouchCancel={handleScratchEnd}
                        onMouseDown={handleScratchStart}
                        onMouseMove={handleScratchMove}
                        onMouseUp={handleScratchEnd}
                        onMouseLeave={handleScratchEnd}
                    />

                    {/* TOUCH BLOCKER OVERLAYS */}
                    {engineState === 'idle' || engineState === 'cover' || engineState === 'game_ready' ? (
                        <div className="scratcher-fan__touch-blocker">
                            <div className="scratcher-fan__lock-card">
                                <div className="scratcher-fan__lock-icon-wrapper">
                                    <i className="bi bi-lock-fill scratcher-fan__lock-icon" />
                                </div>
                                <h3 className="scratcher-fan__lock-title">¡PREPÁRATE!</h3>
                                <p className="scratcher-fan__lock-desc">
                                    La partida está por comenzar...
                                </p>
                            </div>
                        </div>
                    ) : null}

                    {engineState === 'countdown' ? (
                        <div className="scratcher-fan__touch-blocker scratcher-fan__touch-blocker--countdown">
                            <div className="scratcher-fan__countdown-number" key={countdownValue}>
                                {countdownValue}
                            </div>
                            <p className="scratcher-fan__countdown-label">¡LISTOS!</p>
                        </div>
                    ) : null}

                    {engineState === 'paused' ? (
                        <div className="scratcher-fan__touch-blocker scratcher-fan__touch-blocker--paused">
                            <div className="scratcher-fan__lock-card">
                                <i className="bi bi-pause-circle-fill scratcher-fan__lock-icon" />
                                <h3 className="scratcher-fan__lock-title">PARTIDA PAUSADA</h3>
                                <p className="scratcher-fan__lock-desc">Espera a que el operador reanude la dinámica.</p>
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* HERRAMIENTA / MONEDA FLOTANTE (CUSTOM CURSOR) */}
                {showScratchTool && toolPosition.isVisible && engineState === 'playing' ? (
                    <div
                        className="scratcher-fan__scratch-tool"
                        style={{
                            transform: `translate3d(${toolPosition.x}px, ${toolPosition.y}px, 0)`
                        }}
                    >
                        <span className="scratcher-fan__scratch-coin">🪙</span>
                    </div>
                ) : null}
            </main>

            {/* MODAL DE SUSTITUCIÓN DE SESIÓN (PATRÓN TACO) */}
            {isSessionReplaced ? (
                <div className="scratcher-fan__modal-backdrop">
                    <div className="scratcher-fan__modal-card scratcher-fan__modal-card--session-replaced">
                        <div className="scratcher-fan__modal-icon-wrapper">
                            <i className="bi bi-phone scratcher-fan__modal-icon" />
                        </div>
                        <h2 className="scratcher-fan__modal-title">Sesión Transferida</h2>
                        <p className="scratcher-fan__modal-body">
                            {sessionReplacedMessage || 'Tu sesión fue transferida a otro dispositivo. ¡Gracias por participar!'}
                        </p>
                        <button
                            type="button"
                            className="scratcher-fan__modal-btn"
                            onClick={handleSwitchCharacter}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            ) : null}

            {/* MODAL DE FIN DE PARTIDA / RESULTADOS */}
            {isGameOverModalOpen ? (
                <div className="scratcher-fan__modal-backdrop">
                    <div className="scratcher-fan__modal-card scratcher-fan__modal-card--results">
                        <div className="scratcher-fan__results-header">
                            {gameOverPayload?.winner === selectedPlayerId ||
                            gameOverPayload?.winner?.toLowerCase() === activePlayerData?.slot?.toLowerCase() ||
                            gameOverPayload?.winner?.toLowerCase() === activePlayerData?.name?.toLowerCase() ? (
                                <>
                                    <div className="scratcher-fan__results-trophy">🏆</div>
                                    <h2 className="scratcher-fan__results-title scratcher-fan__results-title--win">
                                        ¡VICTORIA!
                                    </h2>
                                    <p className="scratcher-fan__results-subtitle">
                                        ¡Fuiste el más veloz en revelar tu premio!
                                    </p>
                                </>
                            ) : gameOverPayload?.winner === 'Empate' ? (
                                <>
                                    <div className="scratcher-fan__results-trophy">🤝</div>
                                    <h2 className="scratcher-fan__results-title scratcher-fan__results-title--draw">
                                        ¡EMPATE!
                                    </h2>
                                    <p className="scratcher-fan__results-subtitle">¡Gran esfuerzo de ambos aficionados!</p>
                                </>
                            ) : (
                                <>
                                    <div className="scratcher-fan__results-trophy">👏</div>
                                    <h2 className="scratcher-fan__results-title">¡FIN DE PARTIDA!</h2>
                                    <p className="scratcher-fan__results-subtitle">¡Gran intento en la cancha!</p>
                                </>
                            )}
                        </div>

                        <div className="scratcher-fan__results-stats">
                            <div className="scratcher-fan__stat-card">
                                <span className="scratcher-fan__stat-label">Área Revelada</span>
                                <span className="scratcher-fan__stat-value">{scratchedPercentage}%</span>
                            </div>
                            <div className="scratcher-fan__stat-card">
                                <span className="scratcher-fan__stat-label">Raspones</span>
                                <span className="scratcher-fan__stat-value">{strokeCountRef.current}</span>
                            </div>
                            {gameOverPayload?.effectiveDurationSeconds ? (
                                <div className="scratcher-fan__stat-card">
                                    <span className="scratcher-fan__stat-label">Tiempo</span>
                                    <span className="scratcher-fan__stat-value">
                                        {gameOverPayload.effectiveDurationSeconds}s
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
